import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'styles/util';
import { FieldArray } from 'react-final-form-arrays';
import { getDefaultSeoGeoBlock } from 'helpers/SeoGeoContentHelper';

import { TextField } from 'mui-rff';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';

import SeoBlockText from 'components/SeoBlockText';
import SeoBlockCta from 'components/SeoBlockCta';
import SeoBlockFaq from 'components/SeoBlockFaq';
import SeoBlockHeading from 'components/SeoBlockHeading';

const useStyles = makeStyles(({ theme, colors }) => ({
	item: {
		padding: theme.spacing(2, 1),
		marginBottom: theme.spacing(2),
		border: `1px solid ${colors.darkGrey}`,
		borderRadius: theme.spacing(1),
		backgroundColor: colors.lightestGrey,
		'& > *': {
			padding: theme.spacing(0, 1)
		}
	},
	deleteButtonContainer: {
		textAlign: 'right',
		alignSelf: 'end'
	},
	blocksContainer: {
		border: `1px solid ${colors.grey}`,
		borderRadius: theme.spacing(0.5),
		padding: theme.spacing(1)
	},
	blockItem: {
		border: `1px solid ${colors.grey}`,
		borderRadius: theme.spacing(0.5),
		padding: theme.spacing(1),
		marginBottom: theme.spacing(1)
	},
	divider: {
		margin: theme.spacing(2, 0)
	},
	addFaqItemButton: {
		padding: theme.spacing(1, 2),
	},
	deleteButton: {
		color: colors.red,
		borderColor: colors.red,
		padding: theme.spacing(1, 2),
		'&:hover': {
			borderColor: colors.red,
			backgroundColor: colors.red,
			color: colors.white
		}
	}
}));

const SeoGeoItemEditor = ({ name, onRemoveItem, onBlur }) => {
	const classes = useStyles();

	const handleAddBlock = blockFields => {
		blockFields.push(getDefaultSeoGeoBlock());
	};

	const handleBlockTypeChange = (blockFields, index, blockType) => {
		let block = getDefaultSeoGeoBlock();

		if (blockType === 'faq') {
			block = { type: 'faq', faqItems: [{ question: '', answer: '' }] };
		} else if (blockType === 'cta') {
			block = { type: 'cta', label: '', url: '' };
		} else if (blockType === 'heading') {
			block = { type: 'heading', level: 'h2', text: '' };
		}

		blockFields.update(index, block);
	};

	const renderBlockFields = (blockName, blockType) => {
		if (blockType === 'faq') {
			return (
				<SeoBlockFaq
					blockName={blockName}
					onBlur={onBlur}
				/>
			);
		}

		if (blockType === 'cta') {
			return (
				<SeoBlockCta
					blockName={blockName}
					onBlur={onBlur}
				/>
			);
		}

		if (blockType === 'heading') {
			return (
				<SeoBlockHeading
					blockName={blockName}
					onBlur={onBlur}
				/>
			);
		}

		return (
			<SeoBlockText
				blockName={blockName}
				onBlur={onBlur}
			/>
		);
	};

	return (
		<Grid container className={classes.item}>
			<Grid item xs={12} md={6}>
				<TextField
					name={`${name}.location`}
					label="Location"
					variant="outlined"
					size="small"
					onBlur={onBlur}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					name={`${name}.h1`}
					label="H1"
					variant="outlined"
					size="small"
					onBlur={onBlur}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					name={`${name}.title`}
					label="Title"
					variant="outlined"
					size="small"
					onBlur={onBlur}
				/>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextField
					name={`${name}.metaDescription`}
					label="Meta description"
					variant="outlined"
					size="small"
					onBlur={onBlur}
				/>
			</Grid>
			<Grid item xs={10} md={11}>
				<FieldArray name={`${name}.blocks`}>
					{({ fields: blockFields }) => (
						<Box className={classes.blocksContainer}>
							<Typography variant="subtitle2" gutterBottom>Content blocks</Typography>
							{blockFields.map((blockName, blockIndex) => {
								const block = blockFields.value && blockFields.value[blockIndex] ? blockFields.value[blockIndex] : getDefaultSeoGeoBlock();
								const blockType = block.type || 'text';

								return (
									<Grid key={blockName} container spacing={1} className={classes.blockItem}>
										<Grid item xs={12} md={4}>
											<TextField
												select
												name={`${blockName}.type`}
												label="Block type"
												variant="outlined"
												size="small"
												onChange={event => handleBlockTypeChange(blockFields, blockIndex, event.target.value)}
											>
												<MenuItem value="text">Text</MenuItem>
												<MenuItem value="heading">Heading</MenuItem>
												<MenuItem value="faq">FAQ</MenuItem>
												<MenuItem value="cta">CTA</MenuItem>
											</TextField>
										</Grid>
										<Grid item xs={12} md={8} className={classes.deleteButtonContainer}>
											<Button variant="outlined" className={classes.deleteButton} onClick={() => blockFields.remove(blockIndex)}>
												Remove block
											</Button>
										</Grid>
										{renderBlockFields(blockName, blockType)}
										<Grid item xs={12} md={12}>
											<Divider className={classes.divider} />
										</Grid>
									</Grid>
								);
							})}
							<Button variant="outlined" color="primary" onClick={() => handleAddBlock(blockFields)}>
								Add block
							</Button>
						</Box>
					)}
				</FieldArray>
			</Grid>
			<Grid item xs={2} md={1} className={classes.deleteButtonContainer}>
				<IconButton
					onClick={onRemoveItem}
				>
					<DeleteOutlineOutlinedIcon />
				</IconButton>
			</Grid>
		</Grid>
	);
};

SeoGeoItemEditor.propTypes = {
	name: PropTypes.string.isRequired,
	onRemoveItem: PropTypes.func.isRequired,
	onBlur: PropTypes.func.isRequired
};

export default SeoGeoItemEditor;
